import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ROLE, ACCESS_LEVEL, RIGHTS } from '@constants/user';

export const useHasAccessToAsset = () => {
    const user = useSelector(state => state.auth.user);
    const userLocationsIds = new Set(user.locations);
    const userBrandsIds = new Set(user.brands);

    // INFO: Campaigns, Survey Templates
    const hasAccessToAssetById = useCallback(
        asset => {
            const access = { [RIGHTS.READ]: false, [RIGHTS.WRITE]: false };

            switch (user?.role) {
                case ROLE.SUPER_ADMIN:
                    return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: true };
                case ROLE.ADMIN:
                    // INFO: Brand admin wont see survey templates of other brands
                    if ([ACCESS_LEVEL.GLOBAL, ACCESS_LEVEL.BRAND].includes(user?.accessLevel)) {
                        return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: true };
                    }
                    if (user?.accessLevel === ACCESS_LEVEL.LOCATION) {
                        if (asset.locationId) {
                            const hasAccess = userLocationsIds.has(asset.locationId);
                            return { [RIGHTS.READ]: hasAccess, [RIGHTS.WRITE]: hasAccess };
                        }
                        return access;
                    }
                    return access;
                case ROLE.USER:
                    return {
                        ...access,
                        [RIGHTS.READ]:
                            user?.accessLevel === ACCESS_LEVEL.GLOBAL
                                ? true
                                : asset.locationId
                                ? userLocationsIds.has(asset.locationId)
                                : userBrandsIds.has(asset.brandId),
                    };
                default:
                    return access;
            }
        },
        [user],
    );

    const hasAccessToChannel = useCallback(
        brandId => {
            if (!brandId) return true;
            return (
                user?.role !== ROLE.USER && !(user?.role === ROLE.ADMIN && user?.accessLevel === ACCESS_LEVEL.LOCATION)
            );
        },
        [user],
    );

    const hasAccessToPosts = useCallback(
        locationIds => {
            const assetLocationsIds = new Set(locationIds);
            return (
                [...assetLocationsIds].every(id => userLocationsIds.has(id)) ||
                user?.accessLevel === ACCESS_LEVEL.GLOBAL
            );
        },
        [userLocationsIds],
    );

    const hasAccessToAssetByIds = useCallback(
        asset => {
            const access = { [RIGHTS.READ]: false, [RIGHTS.WRITE]: false };
            const assetBrandsIds = new Set(asset.brands);
            const assetLocationsIds = new Set(asset.locations);
            switch (user?.role) {
                case ROLE.USER:
                    return access;
                case ROLE.SUPER_ADMIN:
                    return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: true };
                case ROLE.ADMIN:
                    if (user.accessLevel === ACCESS_LEVEL.GLOBAL) {
                        return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: true };
                    }
                    if (assetBrandsIds.size > 0) {
                        if (user?.accessLevel === ACCESS_LEVEL.LOCATION) return access;
                        return {
                            [RIGHTS.READ]: true,
                            [RIGHTS.WRITE]: [...assetBrandsIds].every(id => userBrandsIds.has(id)),
                        };
                    }
                    return {
                        [RIGHTS.READ]: true,
                        [RIGHTS.WRITE]: [...assetLocationsIds].every(id => userLocationsIds.has(id)),
                    };
                default:
                    return access;
            }
        },
        [userBrandsIds, userLocationsIds],
    );

    return {
        hasAccessToChannel,
        hasAccessToPosts,
        hasAccessToAssetByIds,
        hasAccessToAssetById,
    };
};
